import Balance from 'dt-common/constants/Balance';
import getAbilityLevel from 'dt-common/isomorphic-helpers/getAbilityLevel';
import getAvailableAbilityPoints from 'dt-common/isomorphic-helpers/getAvailableAbilityPoints';
import * as PIXI from 'pixi.js';
import AbilityIcon from '~/components/common/AbilityIcon';
import Tooltip from '~/components/tooltips/Tooltip';
import Colors from '~/constants/Colors';
import { EditHeroActions } from '~/flux/actions';
import { FluxGetters, GameStateStore, HeroBuildStore, UIStore } from '~/flux/stores';
import text from '~/text';
import CanvasTools from '~/view/CanvasTools';

const AbilityElement = function ({ ability, hero_handle }) {
  PIXI.Container.call(this);

  const { built_hero, roster_hero } = FluxGetters.getHeroByHandle(hero_handle);
  const current_ability_level = getAbilityLevel({
    ability_handle: ability.handle,
    unit_build: built_hero,
  });

  this.getAbility = () => ability;

  const icon = new AbilityIcon(ability);
  this.getIcon = () => icon;

  icon.anchor.x = icon.anchor.y = 0.5;
  this.addChild(icon);

  let _tooltip;

  let ub;
  this.getUpgradeBtn = () => ub;
  let upgradeButtonTween;
  let upgradeButtonInterval;

  let db;
  this.getDowngradeBtn = () => db;
  let downgradeButtonTween;
  let downgradeButtonInterval;

  if (roster_hero.level >= ability.unlockLevel) {
    const lvlField = (this.lvlFIeld = new PIXI.Text(
      `${text('game.level.brief')}${current_ability_level}`,
      {
        fontFamily: 'Courier New',
        fontWeight: 'bold',
        fontSize: CanvasTools.dynamicFontSizeString(26),
        fill: Colors.BRIGHT_YELLOW,
        align: 'center',
        dropShadow: true,
        dropShadowDistance: 2,
      }
    ));
    lvlField.x = -lvlField.width / 2;
    lvlField.y = 15;
    lvlField.hitArea = new PIXI.Rectangle(0, 0, 0, 0);
    this.addChild(lvlField);

    const available_ability_points = getAvailableAbilityPoints({
      built_hero,
      roster_hero: GameStateStore.getAll().gameState.hero_roster[built_hero.hero_handle],
    });

    // make upgrade button
    if (available_ability_points > 0 && current_ability_level < Balance.MAX_ABILITY_LEVEL) {
      if (HeroBuildStore.getAll().pending_ability_upgrade_handle === ability.handle) {
        return;
      }

      ub = new PIXI.Graphics();
      const buttonSize = 35;
      const lineWidth = 5;
      const lineLength = 20;

      // Draw green plus sign
      ub.lineStyle(lineWidth, 0x44ff44);
      // Vertical line
      ub.moveTo(0, -lineLength / 2);
      ub.lineTo(0, lineLength / 2);
      // Horizontal line
      ub.moveTo(-lineLength / 2, 0);
      ub.lineTo(lineLength / 2, 0);

      ub.x = this.width / 2;
      ub.y = 0; // this.height / 2.7;

      this.addChild(ub);

      // upgrade button interactions
      ub.interactive = true;
      ub.buttonMode = true;
      ub.hitArea = new PIXI.Rectangle(-buttonSize / 2, -buttonSize / 2, buttonSize, buttonSize);

      ub.tap = ub.click = () => {
        const { current_game_mode, current_game_submode } = UIStore.getAll();
        EditHeroActions.upgradeAbility({
          hero_handle: built_hero.hero_handle,
          ability_handle: ability.handle,
          hero_build_id: built_hero.hero_build_id,
        });
      };
      // mouseover - brighten to yellow
      ub.mouseover = ub.pointerover = () => {
        ub.clear();
        ub.lineStyle(lineWidth, Colors.BRIGHT_YELLOW);
        ub.moveTo(0, -lineLength / 2);
        ub.lineTo(0, lineLength / 2);
        ub.moveTo(-lineLength / 2, 0);
        ub.lineTo(lineLength / 2, 0);
      };
      // mouseout - back to green
      ub.mouseout = ub.pointerout = () => {
        ub.clear();
        ub.lineStyle(lineWidth, 0x44ff44);
        ub.moveTo(0, -lineLength / 2);
        ub.lineTo(0, lineLength / 2);
        ub.moveTo(-lineLength / 2, 0);
        ub.lineTo(lineLength / 2, 0);

        ub.mouseup();

        CanvasTools.hideFaerieSpinners(_spinners);
      };
      // mousedown - scale down
      ub.mousedown = ub.touchstart = () => {
        CanvasTools.hideFaerieSpinners(_spinners);
        _spinners = CanvasTools.makeFaerieSpinners(this);

        ub.scale_buffer = { x: ub.scale.x, y: ub.scale.y };
        ub.scale = { x: ub.scale.x * 0.8, y: ub.scale.y * 0.8 };
      };
      // mouseup - scale up
      ub.mouseup = ub.touchend = () => {
        if (ub.scale_buffer) {
          ub.scale = ub.scale_buffer;
        }
      };
    }

    // make downgrade button
    if (current_ability_level > 0) {
      if (HeroBuildStore.getAll().pending_ability_upgrade_handle === ability.handle) {
        return;
      }

      db = new PIXI.Graphics();
      const buttonSize = 35;
      const lineWidth = 5;
      const lineLength = 20;

      // Draw red minus sign
      db.lineStyle(lineWidth, 0xff6666);
      // Horizontal line only
      db.moveTo(-lineLength / 2, 0);
      db.lineTo(lineLength / 2, 0);

      db.x = -this.width / 2;
      db.y = 0; // this.height / 2.7;

      this.addChild(db);

      // downgrade button interactions
      db.interactive = true;
      db.buttonMode = true;
      db.hitArea = new PIXI.Rectangle(-buttonSize / 2, -buttonSize / 2, buttonSize, buttonSize);

      db.tap = db.click = () => {
        const { current_game_mode, current_game_submode } = UIStore.getAll();
        EditHeroActions.downgradeAbility({
          hero_handle: built_hero.hero_handle,
          ability_handle: ability.handle,
          hero_build_id: built_hero.hero_build_id,
        });
      };
      // mouseover - brighten to brighter red
      db.mouseover = db.pointerover = () => {
        db.clear();
        db.lineStyle(lineWidth, 0xff3333);
        db.moveTo(-lineLength / 2, 0);
        db.lineTo(lineLength / 2, 0);
      };
      // mouseout - back to normal red
      db.mouseout = db.pointerout = () => {
        db.clear();
        db.lineStyle(lineWidth, 0xff6666);
        db.moveTo(-lineLength / 2, 0);
        db.lineTo(lineLength / 2, 0);

        db.mouseup();

        CanvasTools.hideFaerieSpinners(_spinners);
      };
      // mousedown - scale down
      db.mousedown = db.touchstart = () => {
        CanvasTools.hideFaerieSpinners(_spinners);
        _spinners = CanvasTools.makeFaerieSpinners(this);

        db.scale_buffer = { x: db.scale.x, y: db.scale.y };
        db.scale = { x: db.scale.x * 0.8, y: db.scale.y * 0.8 };
      };
      // mouseup - scale up
      db.mouseup = db.touchend = () => {
        if (db.scale_buffer) {
          db.scale = db.scale_buffer;
        }
      };
    }
  } else {
    // this ability is still locked
    icon.tint = 0x444444;

    const unlockIcon = new PIXI.Sprite();
    unlockIcon.texture = PIXI.utils.TextureCache['armory/lock.png'];
    unlockIcon.anchor.x = unlockIcon.anchor.y = 0.5;
    icon.addChild(unlockIcon);

    const text = new PIXI.Text(`(L${ability.unlockLevel})`, {
      fontFamily: 'Courier New',
      fontWeight: 'bold',
      fontSize: CanvasTools.dynamicFontSizeString(14),
      fill: 0xffffff,
      align: 'center',
      dropShadow: true,
      dropShadowDistance: 2,
    });
    text.anchor.x = text.anchor.y = 0.5;
    text.y = icon.height * 0.42;
    text.interactive = true;
    text.buttonMode = true;
    icon.addChild(text);
  }

  // tooltip interactions
  this.buttonMode = this.interactive = true;
  this.mouseover = this.pointerover = () => {
    if (!_tooltip) {
      _tooltip = new Tooltip(
        'ability',
        {
          ability_handle: ability.handle,
          locked: roster_hero.level < ability.unlockLevel,
        },
        this
      );
    }
  };
  this.mouseout = this.pointerout = () => {
    if (_tooltip) {
      _tooltip.dispose();
      _tooltip = null;
    }
  };

  let _spinners = [];
  // const makeSpinners = () => {
  //   if (!this.parent || HeroBuildStore.getAll().pending_ability_upgrade_handle) {
  //     return;
  //   }

  //   CanvasTools.hideFaerieSpinners(_spinners);
  //   _spinners = CanvasTools.makeFaerieSpinners(this);
  // };

  this.dispose = () => {
    this.mouseover = this.pointerover = null;
    this.mouseout = this.pointerout = null;

    if (icon) {
      icon.dispose();
    }

    if (_tooltip) {
      _tooltip.dispose();
    }

    if (ub) {
      ub.mouseover = ub.pointerover = null;
      ub.mouseout = ub.pointerout = null;
      ub.mousedown = ub.touchstart = null;
      ub.mouseup = ub.touchend = null;
    }

    if (db) {
      db.mouseover = db.pointerover = null;
      db.mouseout = db.pointerout = null;
      db.mousedown = db.touchstart = null;
      db.mouseup = db.touchend = null;
    }

    if (upgradeButtonTween) {
      upgradeButtonTween.kill();
      upgradeButtonTween = null;
    }

    if (downgradeButtonTween) {
      downgradeButtonTween.kill();
      downgradeButtonTween = null;
    }

    CanvasTools.hideFaerieSpinners(_spinners);
    _spinners = null;

    clearInterval(upgradeButtonInterval);
    clearInterval(downgradeButtonInterval);

    DT_CANVAS_GLOBALS.poolSafeDestroy(this, { children: true });
  };
};
AbilityElement.prototype = Object.create(PIXI.Container.prototype);
AbilityElement.prototype.constructor = AbilityElement;
export default AbilityElement;
